import React, { useState, useRef } from 'react';
import { Box, Grid, Typography, Button, TextField, Alert } from '@mui/material';
import { Add } from '@mui/icons-material';
// import 'react-quill/dist/quill.snow.css';
import RichTextEditor from './RichTextEditor';
import { useTheme } from '@mui/material/styles';
import DOMPurify from 'dompurify';

interface Insight {
  id: number;
  title: string;
  description: string;
  // createdAt: string;
}
interface Insights {
  insightId: number;
  title: string;
  description: string;
  opsType: string;
  createdBy: string;
  updatedBy: string;
  createdDate: string;
  updatedDate: string;
}
const formatCSTTime = (isoDate: string) => {
  const date = new Date(isoDate);
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'America/Chicago',
  };
  return `Created: ${date.toLocaleString('en-US', options)} CST`;
};

const modules = {
  toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['image']],
};

const formats = ['bold', 'italic', 'underline', 'list', 'bullet', 'indent', 'image'];

const containsXssContent = (input: string): boolean => {
  if (!input) return false;
  const patterns = [
    /<\s*script\b/i,
    /&lt;\s*script\b/i,
    /<\s*\/\s*script\s*>/i,
    /&lt;\s*\/\s*script\s*&gt;/i,
    /<\s*iframe\b/i,
    /&lt;\s*iframe\b/i,
    /<\s*object\b/i,
    /&lt;\s*object\b/i,
    /<\s*embed\b/i,
    /&lt;\s*embed\b/i,
    /\bon\w+\s*=/i,
    /&lt;[^>]*\bon\w+\s*=/i,
    /\bjavascript\s*:/i,
    /\bvbscript\s*:/i,
  ];
  return patterns.some((pattern) => pattern.test(input));
};

const Insights: React.FC<{
  insights: Insights[];
  setInsights: React.Dispatch<React.SetStateAction<Insights[]>>;
  onAdd: (insight: Insight) => void;
  onUpdate: (insight: Insight) => void;
  onDelete: (id: number) => void;
}> = ({ insights, setInsights, onAdd, onUpdate, onDelete }) => {
  const theme = useTheme();
  const [currentInsight, setCurrentInsight] = useState<Insights | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const handleOpen = (insight?: Insights) => {
    setErrorMessage('');
    if (insight) {
      setCurrentInsight(insight);
      setTitle(insight.title);
      setDescription(insight.description);
    } else {
      setCurrentInsight(null);
      setTitle('');
      setDescription('');
    }
    setShowForm(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleSave = () => {
    const trimmedTitle = title.trim();
    const trimmedDesc = description.trim();

    if (!trimmedTitle) {
      setErrorMessage('Title is required.');
      return;
    }

    if (!trimmedDesc || trimmedDesc === '<p></p>') {
      setErrorMessage('Description is required.');
      return;
    }

    if (containsXssContent(title) || containsXssContent(description)) {
      setErrorMessage('Script tags and Cross-Site Scripting (XSS) content are not allowed in Title or Description.');
      return;
    }

    setErrorMessage('');
    const cleanTitle = DOMPurify.sanitize(trimmedTitle, { ALLOWED_TAGS: [] }).trim();
    const cleanDescription = DOMPurify.sanitize(description);

    if (currentInsight) {
      const newInsight: Insight = {
        id: currentInsight.insightId,
        title: cleanTitle,
        description: cleanDescription,
      };

      onUpdate(newInsight);
    } else {
      const newInsight: any = {
        title: cleanTitle,
        description: cleanDescription,
      };
      onAdd(newInsight);
    }

    setShowForm(false);
  };

  const handleDelete = (InsightId: number) => {
    onDelete(InsightId);
  };

  const formatDescription = (description: string) => {
    if (!description) return '';
    const formatted = description.replace(
      /<img /g,
      '<img style="width: 100%; max-width: 100%; height: auto; display: block; margin-top: 8px;" ',
    );
    return DOMPurify.sanitize(formatted);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        {insights.map((insight: any) => {
          return (
            <Grid
              container
              key={insight.insightId}
              sx={{
                pb: 1,
                marginBottom: 2,
                borderBottom: `2px solid ${theme.palette.text.subtle}`,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                sx={{ marginBottom: 1 }}
              >
                <Typography fontSize="20px" fontWeight={700} color={theme.palette.textLight.main}>
                  {insight.title}
                </Typography>
                <Typography
                  variant="body2"
                  fontStyle="italic"
                  fontSize={12}
                  color={theme.palette.text.subtle}
                >
                  {formatCSTTime(insight.updatedDate)}
                </Typography>
              </Grid>
              <Box
                sx={{
                  width: '100%',
                  '& table': {
                    borderCollapse: 'collapse',
                    width: '100%',
                    backgroundColor: 'transparent',
                  },
                  '& th, & td': {
                    px: '8px',
                    color: `${theme.palette.textLight.main}`,
                    border: `2px solid ${theme.palette.textLight.secondary}`,
                    backgroundColor: 'transparent',
                    textAlign: 'left',
                  },
                  '& th': {
                    fontWeight: 'bold',
                  },
                  // maxWidth: '800px',
                  // height: '300px',
                  // overflowY: 'auto',
                  marginBottom: 2,
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: formatDescription(insight.description) }}
                  style={{ width: '100%', color: theme.palette.textLight.main }}
                />
              </Box>
              <Box
                sx={{
                  marginTop: 1,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'flex-end',
                }}
              >
                <Button
                  sx={{ color: '#D82B51', fontWeight: '700' }}
                  onClick={() => handleDelete(insight.insightId)}
                >
                  DELETE
                </Button>
                {!showForm && (
                  <Button
                    sx={{ color: `${theme.palette.textLight.secondary}`, fontWeight: '700' }}
                    onClick={() => handleOpen(insight)}
                  >
                    UPDATE
                  </Button>
                )}
              </Box>
            </Grid>
          );
        })}
        {!showForm && (
          <Button
            variant="pepwiseButtonText"
            startIcon={<Add sx={{ color: `${theme.palette.buttons.primary.color} !important` }} />}
            onClick={() => handleOpen()}
            sx={{ color: `${theme.palette.buttons.primary.color} !important`, mb: 4 }}
          >
            Add Insights/Actions
          </Button>
        )}
      </Grid>

      {showForm && (
        <Grid item xs={12} ref={formRef} sx={{ marginTop: 2, borderBottom: 1 }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '4px', fontSize: '13px' }}>
              {errorMessage}
            </Alert>
          )}
          <TextField
            fullWidth
            margin="dense"
            placeholder="Title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (errorMessage) setErrorMessage('');
            }}
            sx={{
              marginBottom: '1rem',
              borderRadius: '4px',

              '& .MuiInputBase-input': {
                color: `${theme.palette.textLight.main}`,
                '::placeholder': {
                  color: `${theme.palette.textLight.main}`,
                  opacity: 1,
                },
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: `${theme.palette.text.subtle}`,
                },
                '&:hover fieldset': {
                  borderColor: `${theme.palette.textLight.secondary}`,
                },
                '&.Mui-focused fieldset': {
                  borderColor: `${theme.palette.text.subtle}`,
                },
              },
            }}
          />
          <RichTextEditor value={description} onChange={setDescription} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2, gap: '2rem' }}>
            <Button
              variant="pepwiseButtonText"
              onClick={() => setShowForm(false)}
              sx={{ color: `${theme.palette.buttons.primary.color} !important` }}
            >
              CANCEL
            </Button>
            <Button
              onClick={handleSave}
              variant="pepwiseButtonText"
              sx={{
                backgroundColor: `${theme.palette.buttons.primary.color} !important`,
                padding: '13px 40px',
                '&:disabled': {
                  color: `${theme.palette.buttons.primary.text} !important`,
                  cursor: 'not-allowed',
                },
              }}
              disabled={!title.trim() || !description.trim()}
            >
              {currentInsight ? 'UPDATE' : 'SAVE'}
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default Insights;
